import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type { ActionCategory, ConnectionDetail } from "@typedoc/contracts/connections";
import { Button, Dialog, Loader, Menu, Skeleton, Tabs } from "@typedoc/ui";
import { ErrorNotice } from "../error-notice";
import { normalizeAppError } from "../../lib/app-error";
import {
  disconnectConnection,
  pauseConnection,
  resumeConnection,
  setConnectionAvailableInChat,
  startAttempt,
} from "../../lib/connections/api";
import { rememberAttemptWorkspace } from "../../lib/connections/attempt-workspace";
import {
  EMPTY_DRAFT,
  rebaseDraft,
  type PermissionsDraft,
} from "../../lib/connections/permissions-draft";
import { belongsToOthers, ownerCell } from "../../lib/connections/owners";
import { NO_SESSION, useConnectionsScope } from "../../lib/connections/use-connections-scope";
import { useMembersPersonal } from "../../lib/connections/use-members-personal";
import { connectionQueryOptions, connectionsKey } from "../../lib/queries/connections";
import {
  DesktopNotice,
  openSignIn,
  signInReturn,
  signInUnavailable,
  WaitingStep,
} from "./sign-in-steps";
import {
  AppMark,
  cx,
  DisconnectGlyph,
  formatWhen,
  needsAttention,
  PlugGlyph,
  RenewGlyph,
  scopeLabel,
  StatusText,
} from "./parts";
import { EditableName } from "./editable-name";
import { ActivityLog } from "./activity-log";
import { OwnerNotice } from "./members-switch";
import { PermissionsSkeleton, PermissionsTab } from "./permissions-tab";

export const CONNECTION_TABS = ["permissions", "used-by", "activity"] as const;
export type ConnectionTab = (typeof CONNECTION_TABS)[number];
type Renewal = { attemptId: string; redirectUrl: string };

/** The account's own padding inside the pane beside the list. */
const PANE = "px-4 pt-6 pb-12 sm:px-8";

/**
 * Figma 7.17, opened beside the list (Paper "Proposal · Connections list and detail", Oct 17):
 * one account, who it acts as and how healthy it is, with what teammates may do in it. Pausing
 * is open to the people who manage it, and a personal account is managed by its owner alone.
 *
 * An owner or admin who turned on the members' switch can open another member's personal account
 * to look at it: its `can` flags are all false, so no action shows, and its Permissions and
 * Activity are its owner's alone (the API answers NOT_FOUND), so only Used by is there.
 */
export function ConnectionDetails({
  connectionId,
  tab,
  onTabChange,
}: {
  connectionId: string;
  tab: ConnectionTab;
  onTabChange: (tab: ConnectionTab) => void;
}) {
  const { context, blocker } = useConnectionsScope();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [renewal, setRenewal] = useState<Renewal | null>(null);
  const menuTrigger = useRef<HTMLButtonElement>(null);
  // Focus goes back to whichever control asked to disconnect; a menu item is gone by then, so
  // the menu's trigger stands in for it.
  const disconnectOpener = useRef<HTMLElement | null>(null);
  const [desktopNotice, setDesktopNotice] = useState<ReturnType<typeof signInUnavailable>>(null);
  // Held here rather than in the Permissions panel, which unmounts when another tab is chosen.
  const [draft, setDraft] = useState<PermissionsDraft>(EMPTY_DRAFT);
  const session = context ?? NO_SESSION;
  const members = useMembersPersonal();
  const membersSearch = members.on ? { members: true } : {};
  const detail = useQuery({
    ...connectionQueryOptions(session, connectionId, members.options),
    enabled: Boolean(context) && members.ready,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: connectionsKey(session.scope) });

  const pause = useMutation({
    mutationFn: () => pauseConnection(session, connectionId),
    onSuccess: refresh,
  });
  const resume = useMutation({
    mutationFn: () => resumeConnection(session, connectionId),
    onSuccess: refresh,
  });
  // Use in chat saves at once, not with the Permissions draft, and carries the revision the page
  // read, so a change made elsewhere meanwhile is refused, and the account is read again, rather
  // than overwritten.
  const availability = useMutation({
    mutationFn: (change: { revision: number; availableInChat: boolean }) =>
      setConnectionAvailableInChat(session, connectionId, change),
    onSuccess: async (next, sent) => {
      queryClient.setQueryData(connectionQueryOptions(session, connectionId).queryKey, next);
      // The flip moved the revision on, so a pending Permissions draft follows it rather than
      // turning into a conflict the person did not cause. The mutation stays pending until the
      // account has been read again (`refresh` is awaited), and the Permissions controls hold
      // still until then, so no edit can begin on the revision from before the flip.
      setDraft((current) => rebaseDraft(current, sent.revision, next.revision));
      await refresh();
    },
    onError: async (error) => {
      if (normalizeAppError(error).code === "CONFLICT") await refresh();
    },
  });
  const renew = useMutation({
    mutationFn: (connection: ConnectionDetail) =>
      startAttempt(session, {
        app: connection.app.slug,
        scope: connection.scope,
        connectionId,
        returnTo: signInReturn(),
      }),
    onSuccess: (attempt) => {
      rememberAttemptWorkspace(attempt.id, session.scope.workspaceId);
      openSignIn(attempt.redirectUrl);
      setRenewal({ attemptId: attempt.id, redirectUrl: attempt.redirectUrl });
    },
  });
  const disconnect = useMutation({
    mutationFn: () => disconnectConnection(session, connectionId),
    onSuccess: async () => {
      setConfirming(false);
      await refresh();
      await navigate({ to: "/connections", search: membersSearch });
    },
  });

  /** Renewing in an older desktop shell could never finish, so it says where it works instead. */
  function requestRenew(target: ConnectionDetail) {
    const unavailable = signInUnavailable();
    if (unavailable) setDesktopNotice(unavailable);
    else renew.mutate(target);
  }

  /** From an Activity row to the rule that decided it, on the Permissions tab. */
  function openRule(category: ActionCategory) {
    onTabChange("permissions");
    requestAnimationFrame(() =>
      document
        .getElementById(`category-${category}`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" }),
    );
  }

  function askToDisconnect(opener: HTMLElement | null) {
    disconnectOpener.current = opener;
    setConfirming(true);
  }

  const connection = detail.data;
  const actionError = pause.error ?? resume.error ?? renew.error ?? availability.error;
  // Someone else's account waits for their name too, so its line never says "former member" first.
  const theirs = connection ? belongsToOthers(connection) : false;
  if (!context || !connection || (theirs && members.available && members.people.isPending)) {
    // The list above already says when no workspace is open, so this only adds its own failure.
    if (blocker) return null;
    return detail.error ? (
      <section className={PANE}>
        <ErrorNotice error={normalizeAppError(detail.error)} />
      </section>
    ) : (
      <DetailsSkeleton />
    );
  }

  const live = connection.status !== "disconnected";
  const paused = connection.status === "paused";
  const attention = needsAttention(connection.status);
  const canResume = live && paused && connection.can.manage;
  const canPause = live && !paused && connection.can.pause;
  const canRenew = live && connection.can.manage;
  const canDisconnect = live && connection.can.pause;
  // Permissions and Activity are the owner's alone; only Used by is open to anyone else.
  const shownTab: ConnectionTab = theirs ? "used-by" : tab;

  return (
    // Named by the account, which stays true while its heading turns into the rename field.
    <section aria-label={connection.label} className={PANE}>
      {/* On a phone the actions take their own line, so the name keeps the width it needs. */}
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
        <AppMark app={connection.app} size={44} />
        <div className="min-w-0 flex-1">
          <EditableName
            connection={connection}
            context={context}
            editable={live && connection.can.manage}
            options={members.options}
          />
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]/5 text-muted-foreground">
            <StatusText status={connection.status} className="text-foreground" />
            <span className="inline-flex items-center gap-1.5">
              <span className="text-subtle-foreground">
                <PlugGlyph size={14} />
              </span>
              Direct connection
            </span>
            <span>{scopeLabel(connection.scope)}</span>
            {connection.providerIdentity && (
              <span className="break-all">Acts as {connection.providerIdentity}</span>
            )}
            {connection.checkedAt && <span>Checked {formatWhen(connection.checkedAt)}</span>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 max-sm:w-full max-sm:pl-15">
          {canPause && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => pause.mutate()}
              disabled={pause.isPending}
            >
              Pause
            </Button>
          )}
          {canResume && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => resume.mutate()}
              disabled={resume.isPending}
            >
              Resume
            </Button>
          )}
          {/* Needing attention, the notice under the title carries Renew; one button is enough. */}
          {canRenew && !attention && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => requestRenew(connection)}
              disabled={renew.isPending}
              className="max-sm:hidden"
            >
              Renew access
            </Button>
          )}
          {(canDisconnect || canRenew) && (
            <Menu.Root>
              <Menu.Trigger
                ref={menuTrigger}
                aria-label="More actions"
                className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                  fill="currentColor"
                >
                  <circle cx="3.5" cy="8" r="1.25" />
                  <circle cx="8" cy="8" r="1.25" />
                  <circle cx="12.5" cy="8" r="1.25" />
                </svg>
              </Menu.Trigger>
              <Menu.Content align="end" className="workspace-dialog w-48">
                {canRenew && (
                  <Menu.Item className="sm:hidden" onClick={() => requestRenew(connection)}>
                    <RenewGlyph />
                    Renew access
                  </Menu.Item>
                )}
                {canDisconnect && (
                  <Menu.Item
                    className="text-destructive"
                    onClick={() => askToDisconnect(menuTrigger.current)}
                  >
                    <DisconnectGlyph />
                    Disconnect
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Root>
          )}
          <Link
            to="/connections"
            search={membersSearch}
            aria-label="Close account"
            className="hidden size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground min-[1200px]:grid"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
              <path
                d="M3 3l8 8M11 3l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </Link>
        </div>
      </div>
      {attention && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
          <p className="text-[13px]/5 text-body-foreground">
            {connection.status === "revoked"
              ? `${connection.app.name} no longer lets Abacor in. Teammates can't use it until someone signs in again.`
              : `${connection.app.name} asked for a fresh sign-in. Teammates can't use it until then.`}
          </p>
          {canRenew && (
            <Button size="sm" onClick={() => requestRenew(connection)} disabled={renew.isPending}>
              Renew access
            </Button>
          )}
        </div>
      )}
      {paused && !canResume && (
        <p className="mt-5 rounded-xl border border-border bg-secondary px-4 py-3 text-[13px]/5 text-body-foreground">
          Paused.{" "}
          {connection.scope === "workspace"
            ? "An owner, an admin or the person who connected it can turn it back on."
            : "Only its owner can turn it back on."}
        </p>
      )}
      {theirs && (
        <OwnerNotice
          owner={ownerCell(connection, members.owners, members.ownersKnown)}
          className="mt-5"
        />
      )}
      {desktopNotice && <DesktopNotice notice={desktopNotice} className="mt-5" />}
      <ErrorNotice error={actionError ? normalizeAppError(actionError) : null} className="mt-5" />

      <Tabs.Root
        value={shownTab}
        onValueChange={(value) => onTabChange(value as ConnectionTab)}
        className="mt-7"
      >
        <Tabs.List>
          {!theirs && <Tabs.Tab value="permissions">Permissions</Tabs.Tab>}
          <Tabs.Tab value="used-by">Used by</Tabs.Tab>
          {!theirs && <Tabs.Tab value="activity">Activity</Tabs.Tab>}
        </Tabs.List>
        {!theirs && (
          <Tabs.Panel value="permissions">
            {live ? (
              <PermissionsTab
                connection={connection}
                context={context}
                draft={draft}
                onDraftChange={setDraft}
                availability={{
                  checked: availability.isPending
                    ? (availability.variables?.availableInChat ?? connection.availableInChat)
                    : connection.availableInChat,
                  saving: availability.isPending,
                  onChange: (availableInChat) =>
                    availability.mutate({ revision: connection.revision, availableInChat }),
                }}
              />
            ) : (
              <p className="text-[13px]/5 text-muted-foreground">
                This account is disconnected, so nobody can use it. Its history stays under
                Activity.
              </p>
            )}
          </Tabs.Panel>
        )}
        <Tabs.Panel value="used-by">
          <div className="rounded-xl border border-border bg-card px-5 py-6">
            <p className="text-[13px]/5 font-medium">No playbooks use this account yet</p>
            <p className="mt-1 text-[13px]/5 text-muted-foreground">
              When one does, it shows here with the teammate who runs it.
            </p>
          </div>
        </Tabs.Panel>
        {!theirs && (
          <Tabs.Panel value="activity">
            <ActivityLog connection={connection} context={context} onOpenRule={openRule} />
          </Tabs.Panel>
        )}
      </Tabs.Root>

      <Dialog.Root open={confirming} onOpenChange={setConfirming}>
        <Dialog.Popup className="workspace-dialog w-[440px] p-6" finalFocus={disconnectOpener}>
          <Dialog.Title>Disconnect {connection.label}?</Dialog.Title>
          <Dialog.Description className="mt-2">
            Every playbook that uses it loses its {connection.app.name} access at once. Its history
            stays here. To use it again, connect it again.
          </Dialog.Description>
          <ErrorNotice
            error={disconnect.error ? normalizeAppError(disconnect.error) : null}
            className="mt-4"
          />
          <Dialog.Actions className="mt-6">
            <Dialog.Close render={<Button variant="outline" />}>Keep it</Dialog.Close>
            <Button
              variant="destructive"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
            >
              {disconnect.isPending ? <Loader /> : null}
              Disconnect
            </Button>
          </Dialog.Actions>
        </Dialog.Popup>
      </Dialog.Root>

      <Dialog.Root open={renewal !== null} onOpenChange={(open) => !open && setRenewal(null)}>
        <Dialog.Popup className="workspace-dialog w-[520px]">
          {renewal && (
            <WaitingStep
              key={renewal.attemptId}
              app={connection.app}
              attemptId={renewal.attemptId}
              redirectUrl={renewal.redirectUrl}
              context={context}
              backLabel="Cancel"
              overLabel="Close"
              identity={connection.providerIdentity}
              onBack={() => setRenewal(null)}
              onConnected={() => {
                setRenewal(null);
                void refresh();
              }}
            />
          )}
        </Dialog.Popup>
      </Dialog.Root>
    </section>
  );
}

/** The details page's own shape while the account loads: its mark, name, facts and tabs. */
export function DetailsSkeleton() {
  return (
    <output aria-label="Loading the account" className={cx(PANE, "block")}>
      <span className="sr-only">Loading the account…</span>
      <span className="flex items-start gap-4">
        <Skeleton className="size-11 rounded-[22%]" />
        <span className="flex flex-1 flex-col gap-2.5 pt-1">
          <Skeleton className="h-8 w-[min(18rem,70%)] rounded-md" />
          <Skeleton className="h-3.5 w-[min(24rem,85%)]" />
        </span>
      </span>
      <span className="mt-9 flex gap-6 border-b border-border pb-3">
        {["permissions", "used-by", "activity"].map((tab, index) => (
          <Skeleton key={tab} className="h-3.5" style={{ width: [84, 62, 60][index] }} />
        ))}
      </span>
      <PermissionsSkeleton className="mt-5" />
    </output>
  );
}
